import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { Bell, X } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { devError } from "../utils/devLogger";

const PushOptIn = () => {
  const [show, setShow] = useState(false);
  const [vapidKey, setVapidKey] = useState(null);

  useEffect(() => {
    // Only show if: browser supports push, not already asked, not already subscribed
    const dismissed = sessionStorage.getItem("lumifit_push_dismissed");
    if (dismissed) return;
    if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) return;
    if (Notification.permission === "granted" || Notification.permission === "denied") return;

    // Fetch VAPID key from server
    axios.get(`${API}/push/vapid-key`)
      .then(res => {
        if (res.data.public_key) {
          setVapidKey(res.data.public_key);
          // Delay showing the prompt to avoid overwhelming new users
          const timer = setTimeout(() => setShow(true), 8000);
          return () => clearTimeout(timer);
        }
      })
      .catch(() => {}); // Push not configured — silently skip
  }, []);

  const handleAllow = useCallback(async () => {
    if (!vapidKey) {
      toast.error("Push notifications not available");
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast.error("Notifications permission denied");
        setShow(false);
        sessionStorage.setItem("lumifit_push_dismissed", "true");
        return;
      }

      // Register service worker if not already
      const registration = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // Subscribe with VAPID key
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      // Send subscription to backend
      await axios.post(`${API}/reminders/subscribe`, {
        subscription: subscription.toJSON(),
      });

      toast.success("Notifications enabled!");
      setShow(false);
      sessionStorage.setItem("lumifit_push_dismissed", "true");
    } catch (err) {
      devError("Push subscription failed:", err);
      toast.error("Failed to enable notifications. Please try again.");
    }
  }, [vapidKey]);

  const handleDismiss = () => {
    setShow(false);
    sessionStorage.setItem("lumifit_push_dismissed", "true");
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-20 sm:bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:w-96 z-50 glass-card p-4 shadow-xl border border-sky-100 dark:border-sky-800/30 animate-in slide-in-from-bottom" data-testid="push-opt-in">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-sky-800 dark:text-sky-200">Stay on track</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Get gentle daily reminders for your check-in and streak updates.</p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={handleAllow} className="glow-button text-xs px-4" data-testid="push-allow-btn">
              Allow
            </Button>
            <Button size="sm" variant="ghost" onClick={handleDismiss} className="text-xs text-slate-400" data-testid="push-dismiss-btn">
              Not now
            </Button>
          </div>
        </div>
        <button onClick={handleDismiss} className="text-slate-300 hover:text-slate-500 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Convert a URL-safe base64 VAPID key to Uint8Array for pushManager.subscribe()
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default PushOptIn;
